import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useCreateDiscussion } from "@/hooks/useDiscussions";
import { DiscussionImageUpload } from "./DiscussionImageUpload";
import { DiscussionLinkInput, validateLinkUrl } from "./DiscussionLinkInput";

interface CreateDiscussionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  courseId: string;
  moduleId?: string | null;
}

export function CreateDiscussionDialog({
  open,
  onOpenChange,
  courseId,
  moduleId,
}: CreateDiscussionDialogProps) {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkError, setLinkError] = useState<string | null>(null);
  const { toast } = useToast();
  const createMutation = useCreateDiscussion();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!title.trim() || !content.trim()) {
      toast({
        title: "Validation Error",
        description: "Please fill in both title and content.",
        variant: "destructive",
      });
      return;
    }

    const urlError = validateLinkUrl(linkUrl);
    if (urlError) {
      setLinkError(urlError);
      return;
    }

    try {
      await createMutation.mutateAsync({
        courseId,
        moduleId,
        title: title.trim(),
        content: content.trim(),
        image_url: imageUrl || undefined,
        link_url: linkUrl.trim() || undefined,
      });

      toast({
        title: "Discussion created",
        description: "Your discussion has been posted.",
      });

      setTitle("");
      setContent("");
      setImageUrl(null);
      setLinkUrl("");
      setLinkError(null);
      onOpenChange(false);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create discussion. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Start a Discussion</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="title">Title</Label>
            <Input
              id="title"
              placeholder="What would you like to discuss?"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="content">Content</Label>
            <Textarea
              id="content"
              placeholder="Share your thoughts, questions, or insights..."
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={5}
            />
          </div>

          <DiscussionImageUpload
            imageUrl={imageUrl}
            onImageChange={setImageUrl}
            disabled={createMutation.isPending}
          />

          <DiscussionLinkInput
            linkUrl={linkUrl}
            onLinkChange={(v) => { setLinkUrl(v); setLinkError(null); }}
            error={linkError || undefined}
            disabled={createMutation.isPending}
          />

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={createMutation.isPending}>
              {createMutation.isPending ? "Posting..." : "Post Discussion"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
